/**
 * Una cotizacion guardada (COT-01): lo que se le cotizo a quien, y desde aca
 * se vuelve a bajar el PDF, se duplica para corregirla o se elimina.
 *
 * No se edita: el PDF que se descarga hoy tiene que ser el mismo que recibio
 * el cliente. Eliminar pide confirmacion dentro del mismo modal.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { Cotizacion } from '@bandu/compartido';

import { usePeticion } from '../../hooks/use-peticion';
import {
  descargarPdfCotizacion,
  eliminarCotizacion,
  obtenerCotizacion,
} from '../../servicios/cotizaciones.servicio';
import { fechaHoraCompleta, numero, pesos } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';
import { AvisoError, Cargando } from '../comunes/EstadoPeticion';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { Modal } from './Modal';
import { TotalesCotizacion } from './TotalesCotizacion';

export interface ModalCotizacionProps {
  id: number | null;
  onCerrar(): void;
  onEliminada(cotizacion: Cotizacion): void;
}

function sinCotizacion(): Promise<null> {
  return Promise.resolve(null);
}

function DetalleCotizacion({ cotizacion }: { cotizacion: Cotizacion }) {
  const { cliente } = cotizacion;
  const datos: Array<[string, string | null]> = [
    ['Cliente', cliente.nombre],
    ['RUT', cliente.rut],
    ['Contacto', cliente.contacto],
    ['Teléfono', cliente.telefono],
    ['Correo', cliente.email],
    ['Validez', `${cotizacion.validezDias} días`],
    ['Armada por', cotizacion.creadaPor],
  ];

  return (
    <div className="detalle-cotizacion">
      <dl className="datos-pedido">
        {datos.map(([rotulo, valor]) =>
          valor !== null ? (
            <div key={rotulo}>
              <dt>{rotulo}</dt>
              <dd>{valor}</dd>
            </div>
          ) : null,
        )}
      </dl>

      <ul className="items-pedido">
        {cotizacion.items.map((item) => (
          <li key={item.sku} className="item-pedido">
            {item.producto?.imagen ? (
              <Imagen
                className="mini-img"
                src={item.producto.imagen.url}
                alt=""
                ancho={item.producto.imagen.ancho}
                alto={item.producto.imagen.alto}
              />
            ) : (
              <span className="mini-img sin-foto" aria-hidden="true" />
            )}
            <div className="item-pedido-nombre">
              <span className="n">{item.nombre}</span>
              <span className="s mono">{item.sku}</span>
              {item.producto === null ? (
                <span className="etq etq-critico item-pedido-fuera">Ya no esta en el catalogo</span>
              ) : null}
            </div>
            <div className="item-pedido-montos">
              <span className="s">
                {numero(item.cantidad)} x {pesos(item.precioUnitario)}
              </span>
              <span className="num">{pesos(item.precioUnitario * item.cantidad)}</span>
            </div>
          </li>
        ))}
      </ul>

      <TotalesCotizacion totales={cotizacion.totales} descuentoPorcentaje={cotizacion.descuentoPorcentaje} />

      {cotizacion.observaciones !== null ? (
        <p className="parrafo-panel observaciones-cotizacion">{cotizacion.observaciones}</p>
      ) : null}
    </div>
  );
}

export function ModalCotizacion({ id, onCerrar, onEliminada }: ModalCotizacionProps) {
  const { estado, datos, error } = usePeticion(
    (senal) => (id === null ? sinCotizacion() : obtenerCotizacion(id, senal)),
    [id ?? 0],
  );

  const [ocupado, setOcupado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  function cerrar(): void {
    if (ocupado) return;
    setConfirmando(false);
    setFalla(null);
    onCerrar();
  }

  async function descargar(cotizacion: Cotizacion): Promise<void> {
    setOcupado(true);
    setFalla(null);
    try {
      await descargarPdfCotizacion(cotizacion);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setOcupado(false);
    }
  }

  async function eliminar(cotizacion: Cotizacion): Promise<void> {
    setOcupado(true);
    setFalla(null);
    try {
      await eliminarCotizacion(cotizacion.id);
      setConfirmando(false);
      onEliminada(cotizacion);
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal
      titulo={datos !== null ? `Cotización ${datos.numero}` : 'Cotización'}
      bajada={datos !== null ? `Hecha el ${fechaHoraCompleta(datos.creadoEn)}` : undefined}
      abierto={id !== null}
      ancho="extra"
      onCerrar={cerrar}
    >
      {estado === 'cargando' ? <Cargando que="la cotizacion" /> : null}
      {estado === 'error' ? <AvisoError error={error} /> : null}

      {datos !== null ? (
        <>
          <DetalleCotizacion cotizacion={datos} />

          {falla !== null ? (
            <p className="acceso-error" role="alert">
              <Icono nombre="alerta" />
              <span>{falla}</span>
            </p>
          ) : null}

          {confirmando ? (
            <div className="modal-acciones">
              <p className="parrafo-panel">
                ¿Eliminar la cotización <strong>{datos.numero}</strong>? No se puede deshacer.
              </p>
              <button
                type="button"
                className="btn btn-contorno"
                disabled={ocupado}
                onClick={() => {
                  setConfirmando(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-peligro"
                disabled={ocupado}
                onClick={() => {
                  void eliminar(datos);
                }}
              >
                {ocupado ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          ) : (
            <div className="modal-acciones">
              <button
                type="button"
                className="btn btn-contorno"
                disabled={ocupado}
                onClick={() => {
                  setConfirmando(true);
                }}
              >
                Eliminar
              </button>
              <Link className="btn btn-contorno" to={rutas.administracion.cotizacionNueva(datos.id)}>
                <Icono nombre="copiar" />
                Duplicar
              </Link>
              <button
                type="button"
                className="btn btn-primario"
                disabled={ocupado}
                onClick={() => {
                  void descargar(datos);
                }}
              >
                <Icono nombre="factura" />
                {ocupado ? 'Preparando…' : 'Descargar PDF'}
              </button>
            </div>
          )}
        </>
      ) : null}
    </Modal>
  );
}
